/**
 * P3-1 投流账号管理（admin / director）
 *
 * 流程：选平台 + 填账号名 → 生成 callback URL + token →
 * 弹"配置指引"提示去小红书聚光后台粘贴
 */
import { useState } from 'react';
import { Modal, Select, Input, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap, Avatar, EmptyHint } from '../../components/ui';
// ux9 日期统一出口
import { fmtDate } from '../../lib/format';

export function MarketingAccountsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [newAccount, setNewAccount] = useState<any>(null);

  const { data: accounts } = useQuery<any[]>({
    queryKey: ['marketing-accounts'],
    queryFn: () => $.get('/marketing/accounts'),
  });
  const { data: platforms } = useQuery<any[]>({
    queryKey: ['marketing-platforms'],
    queryFn: () => $.get('/marketing/accounts/platforms'),
  });
  const { data: usersList } = useQuery<any>({
    queryKey: ['users-managers'],
    queryFn: () => $.get('/users?pageSize=200'),
  });
  const allUsers = (usersList?.items ?? usersList ?? []) as any[];
  const userMap = new Map<number, any>();
  allUsers.forEach((u: any) => userMap.set(Number(u.id), u));
  const managerOpts = allUsers
    .filter((u: any) => ['manager', 'director', 'admin'].includes(u.role))
    .map((u: any) => ({ value: Number(u.id), label: u.realName + '（' + u.role + '）' }));

  const create = useMutation({
    mutationFn: (dto: any) => $.post('/marketing/accounts', dto),
    onSuccess: (r: any) => {
      setOpen(false);
      setNewAccount(r);
      qc.invalidateQueries({ queryKey: ['marketing-accounts'] });
    },
    onError: (e: any) => message.error(e?.message ?? '创建失败'),
  });

  const [form, setForm] = useState({
    platformId: 1, accountName: '', ownerUserId: undefined as number | undefined,
    callbackToken: '',  // v1.2-rc2 P0-0：用户从小红书后台粘贴的 Token
  });
  const [editingTokenFor, setEditingTokenFor] = useState<any>(null);
  const [tokenInput, setTokenInput] = useState('');

  // v1.2-rc2 P1-3：回调 URL 优先用后端返回的绝对 URL（含 WEB_BASE_URL），fallback 到同源
  const baseUrl = window.location.origin;
  const fullCallbackUrl = (a: any) =>
    a?.callbackUrl?.startsWith('http')
      ? a.callbackUrl
      : `${baseUrl}${a?.callbackUrl ?? `/api/marketing/xhs-leads-callback?account_id=${a?.id ?? ''}`}`;

  const updateToken = useMutation({
    mutationFn: ({ id, token }: { id: number; token: string }) =>
      $.put(`/marketing/accounts/${id}`, { callbackToken: token }),
    onSuccess: () => {
      message.success('Token 已保存');
      setEditingTokenFor(null); setTokenInput('');
      qc.invalidateQueries({ queryKey: ['marketing-accounts'] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  return (
    <>
      <PageHeader
        breadcrumb={['投流', '账号管理']}
        title="投流账号"
        subtitle="管理小红书 / 抖音 等各平台的投流账号 + 私信线索回调"
        actions={<Btn kind="primary" onClick={() => setOpen(true)}>+ 新增账号</Btn>}
      />
      <div className="work-content">
        <HomeCard title={`已绑定账号（${accounts?.length ?? 0}）`}>
          <TableWrap>
            <table className="ck-table" style={{ width: '100%', minWidth: 1080 }}>
              <thead>
                <tr>
                  <th>账号名</th>
                  <th>平台</th>
                  <th>负责人</th>
                  <th>状态</th>
                  <th>Token</th>
                  <th>回调 URL</th>
                  <th>创建时间</th>
                </tr>
              </thead>
              <tbody>
                {(accounts ?? []).length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24 }}>
                    <EmptyHint arrow={false}>暂无账号，点右上「+ 新增账号」接入第一个小红书账号</EmptyHint>
                  </td></tr>
                )}
                {(accounts ?? []).map((a: any) => {
                  const owner = a.ownerUserId ? userMap.get(Number(a.ownerUserId)) : null;
                  // v1.5 批5：平台显示真名（小红书/抖音 等），之前只显示"#1"误以为是占位
                  const plt = (platforms ?? []).find((p) => Number(p.id) === Number(a.platformId));
                  return (
                  <tr key={a.id}>
                    <td>{a.accountName}</td>
                    <td>{plt?.name ?? `平台 #${a.platformId}`}</td>
                    <td>{owner
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Avatar userId={owner.id} src={owner.avatarUrl} name={owner.realName} size="sm" />
                          <span>{owner.realName}</span>
                        </span>
                      : (a.ownerUserId ? '#' + a.ownerUserId : '—')}</td>
                    <td><Pill tone={a.status === 'active' ? 'success' : 'default'}>{a.status}</Pill></td>
                    <td>
                      {/* v1.2-rc2 P0-0：Token 由小红书生成、用户粘贴 — 此列显示是否已配 + 编辑按钮 */}
                      {a.tokenConfigured
                        ? <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            <Pill tone="success">已配置</Pill>
                            <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-3)' }}>{a.tokenTail}</span>
                            <Btn kind="default" onClick={() => { setEditingTokenFor(a); setTokenInput(''); }}>更改</Btn>
                          </span>
                        : <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            <Pill tone="warning">未配置</Pill>
                            <Btn kind="primary" onClick={() => { setEditingTokenFor(a); setTokenInput(''); }}>填入 Token</Btn>
                          </span>}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-3)' }}>
                      {fullCallbackUrl(a)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(a.createdAt)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <Modal
        title="新增投流账号"
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <>
            <Btn kind="default" onClick={() => setOpen(false)}>取消</Btn>
            <Btn kind="primary"
                 onClick={() => create.mutate(form)}
                 disabled={!form.accountName || create.isPending}>
              {create.isPending ? '创建中…' : '创建账号'}
            </Btn>
          </>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>平台</div>
          <Select
            value={form.platformId}
            onChange={(v) => setForm({ ...form, platformId: Number(v) })}
            options={(platforms ?? []).map((p: any) => ({ value: Number(p.id), label: p.name }))}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>账号名（自定义，便于辨认）</div>
          <Input value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })}
                 placeholder="例如：小红书 - 育婴师品类号" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>投流责任人（候选销售员从该 manager 下选取）</div>
          <Select
            value={form.ownerUserId}
            onChange={(v) => setForm({ ...form, ownerUserId: Number(v) })}
            options={managerOpts}
            style={{ width: '100%' }}
            allowClear
            placeholder="可选"
          />
        </div>
        <div>
          {/* v1.2-rc2 P0-0：Token 来自小红书后台，CRM 不再自动生成 */}
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            🔐 小红书回调 Token（可后填）
          </div>
          <Input.Password
            value={form.callbackToken}
            onChange={(e) => setForm({ ...form, callbackToken: e.target.value })}
            placeholder="登录小红书聚光后台→线索推送 API→复制 Token 粘贴到此"
          />
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
            留空也可创建账号，但<strong>没填 Token 时本系统跳过签名验证</strong>（联调用，请尽快回填）
          </div>
        </div>
      </Modal>

      {/* 配置指引弹框：创建后弹 */}
      <Modal
        title="✅ 账号创建成功 — 小红书后台配置指引"
        open={!!newAccount}
        onCancel={() => setNewAccount(null)}
        footer={<Btn kind="primary" onClick={() => setNewAccount(null)}>我已配置</Btn>}
        width={680}
      >
        <Step n={1} title="登录小红书聚光后台">
          访问 <strong style={{ fontFamily: 'monospace' }}>ad.xiaohongshu.com</strong>，
          用品类账号登录（不是个人号）。
        </Step>
        <Step n={2} title="进入「线索推送 API」配置页">
          顶部菜单 <strong>工具</strong> → <strong>线索行业工具</strong> → <strong>线索推送 API</strong>。
          点击右上角"+ 新建配置"。
        </Step>
        <Step n={3} title="① 把 CRM 回调 URL 粘到小红书">
          {/* v1.2-rc2 P1-3：用后端返回的绝对 URL（WEB_BASE_URL 拼好），fallback 同源 */}
          <CopyRow label="📡 复制 → 粘到「POST 接收地址」"
                   value={fullCallbackUrl(newAccount)} />
        </Step>
        <Step n={4} title="② 把小红书 Token 粘回 CRM">
          小红书页面会生成一个 <strong>Token</strong>（形如 <code style={{ background: 'var(--bg-2)', padding: '0 4px' }}>4a4c2e9a4a87843f3ed7a10839f2c927</code>），<br />
          复制后回到 CRM 账号列表，点击 <strong>"填入 Token"</strong> / <strong>"更改"</strong> 粘贴并保存。<br />
          {newAccount?.tokenConfigured
            ? <span style={{ color: 'var(--success)' }}>✓ 已配置（创建时填了）</span>
            : <span style={{ color: 'var(--warning)' }}>⚠ 当前账号尚未配置 Token，请到列表"填入 Token"</span>}
        </Step>
        <Step n={5} title="③ 测试 + 上线">
          小红书点"发送测试消息"，CRM <strong>/marketing/leads</strong> 应立即收到一条 + AI 分析；
          失败时看 <strong>marketing_leads_callback_logs</strong>。<br />
          联调通过后切到"正式推送"。
        </Step>
        <div style={{
          marginTop: 16, padding: 12, background: 'rgba(124,77,255,0.08)',
          borderRadius: 4, fontSize: 12, color: 'var(--text-2)',
        }}>
          💡 <strong>方向</strong>：Token 由小红书生成 → 你粘到 CRM；回调 URL 由 CRM 生成 → 你粘到小红书。<br />
          未配置 Token 期间 CRM 跳过签名验证（仅联调用），上线必须填。
        </div>
      </Modal>

      {/* v1.2-rc2 P0-0：Token 填入/更改 弹框 */}
      <Modal
        title={editingTokenFor?.tokenConfigured ? '更改回调 Token' : '填入小红书回调 Token'}
        open={!!editingTokenFor}
        onCancel={() => { setEditingTokenFor(null); setTokenInput(''); }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setEditingTokenFor(null); setTokenInput(''); }}>取消</Btn>
            <Btn kind="primary" disabled={!tokenInput || updateToken.isPending}
                 onClick={() => updateToken.mutate({ id: editingTokenFor.id, token: tokenInput })}>
              {updateToken.isPending ? '保存中…' : '保存'}
            </Btn>
          </>
        }
      >
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
          账号：<strong>{editingTokenFor?.accountName}</strong>
        </div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>🔐 小红书 Token（HMAC-SHA1 签名密钥）</div>
        <Input.Password
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="例如：4a4c2e9a4a87843f3ed7a10839f2c927"
          onPressEnter={() => tokenInput && updateToken.mutate({ id: editingTokenFor.id, token: tokenInput })}
        />
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
          来源：小红书聚光后台 → 工具 → 线索推送 API → 复制 Token。<br />
          保存后下条推送 CRM 即开始校验 HMAC-SHA1 签名，伪造数据会被拒。
        </div>
      </Modal>
    </>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: any }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
      <div style={{
        width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 600, fontSize: 13, flexShrink: 0,
      }}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{children}</div>
      </div>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => message.success('已复制'));
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input value={value} readOnly style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }} />
        <Btn kind="default" onClick={copy}>📋 复制</Btn>
      </div>
    </div>
  );
}
